import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import xlsx from "json-as-xlsx";
import { USER, XLSX } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import {
  getFieldErrors,
  IFieldError,
  interpretDataIntoXlsxFormat,
  rentalItemExcelData2DBData,
} from "../helpers";
import { tryConfirmLogin } from "./authReducer";
import { ICustomer } from "./customersReducer";
import { IDriver } from "./driversReducer";
import { IServiceResource } from "./servicesResourcesReducer";
import { IStaff } from "./staffsReducer";

class ExportRentalItemsErrs {
  form: string | null = null;
}

export interface IAvailableRentalItem {
  id: number;
  _id: string;
  name: string;
  imgSrc: string;
  pricePerHour: number;
  count: number;
}

export interface IRentalItem {
  _id: string;
  name: string;
  type?: string;
  category?: string;
  subCategory?: string;
  sections?: IServiceResource[];
  description?: string;
  virtualLink?: string;
  activationDate?: string;
  deactivationDate?: string;
  staff?: IStaff;
  rentalPackagesCount?: number;
  imgSrc: string;
  pricePerDay: number;
  count: number;
  rating: number;
  availableCount: number;
  createdAt: string;
  deletedAt?: string;
}

export interface IRentalItemForDelivery {
  _id: string;
  name: string;
  imgSrc: string;
  pricePerHour: number;
  pricePerDay: number;
  plannedDays: number;
  count: number;
  customer: ICustomer;
  driver: IDriver;
}

export interface IRentalItemForPickUp {
  _id: string;
  name: string;
  imgSrc: string;
  pricePerHour: number;
  pricePerDay: number;
  daysRemaining: number;
  count: number;
  customer: ICustomer;
  driver: IDriver | null;
}

export interface IPackage2Deliver {
  items: IRentalItemForDelivery[];
}

export interface IPackage2PickUp {
  item: IRentalItemForPickUp[];
}

class RegRentalItemErrs {
  index?: any;

  form: string | null = null;
  description: string | null = null;
  virtualLink: string | null = null;
  staffId?: string;
  category?: string;
  subCategory?: string;
  name: string | null = null;
  rentalItemImg: string | null = null;
  pricePerDay: string | null = null;
  count: string | null = null;
  activationDate: string | null = null;
  deactivationDate: string | null = null;
}

export interface IRentalItemsState {
  registeringRentalItem: boolean;
  regRentalItemErrs: RegRentalItemErrs;
  fetchingRentalItems: boolean;
  fetchRentalItemsErr: string | null;
  rentalItems: IRentalItem[];
  togglingLike: boolean;
  toggleLikeErr: string | null;
  favouriteRentalItems: IRentalItem[];
  mainRentalItem: IRentalItem | null;
  availableItems: IAvailableRentalItem[];
  updatingRentalItem: boolean;
  updateRentalItemErrs: RegRentalItemErrs;
  exportingRentalItems: boolean | null;
  exportedRentalItems: boolean | null;
  exportRentalItemsErrs: ExportRentalItemsErrs;
  importingRentalItems: boolean;
  triedImportingRentalItems: boolean | null;
  importedRentalItems: boolean | null;
  importRentalItemsErrs: RegRentalItemErrs[];
  deletingRentalItem: boolean;
  deletedRentalItem: boolean;
  deleteRentalItemErr: string | null;
}

const initialState = {
  registeringRentalItem: false,
  regRentalItemErrs: new RegRentalItemErrs(),
  fetchingRentalItems: false,
  fetchRentalItemsErr: null,
  rentalItems: [],
  mainRentalItem: null,
  availableItems: [],
  updatingRentalItem: false,
  updateRentalItemErrs: new RegRentalItemErrs(),
  togglingLike: false,
  toggleLikeErr: null,
  favouriteRentalItems: [],
  exportingRentalItems: false,
  exportedRentalItems: false,
  exportRentalItemsErrs: new ExportRentalItemsErrs(),
  triedImportingRentalItems: false,
  importingRentalItems: false,
  importedRentalItems: false,
  importRentalItemsErrs: [],
  deletingRentalItem: false,
  deletedRentalItem: false,
  deleteRentalItemErr: null,
} as IRentalItemsState;

const rentalItemsSlice = createSlice({
  name: "rentalItems",
  initialState,
  reducers: {
    clearRegRentalItemFieldErr: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.regRentalItemErrs[action.payload as keyof RegRentalItemErrs] = null;
    },
    clearRegRentalItemErrs: (state: IRentalItemsState) => {
      state.regRentalItemErrs = new RegRentalItemErrs();
    },
    tryResetRegRentalItemFormErr: (state: IRentalItemsState) => {
      let shouldReset = true;

      for (let key in state.regRentalItemErrs) {
        if (key === "form") {
          continue;
        }

        if (state.regRentalItemErrs[key as keyof RegRentalItemErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.regRentalItemErrs.form = null;
      }
    },
    regRentalItemStart: (state: IRentalItemsState) => {
      state.registeringRentalItem = true;
    },
    regRentalItemPass: (state: IRentalItemsState) => {
      state.registeringRentalItem = false;
    },
    regRentalItemFail: (
      state: IRentalItemsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.registeringRentalItem = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.regRentalItemErrs) {
          if (err.field === key) {
            state.regRentalItemErrs[key as keyof RegRentalItemErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.regRentalItemErrs.form) {
        state.regRentalItemErrs.form = "Form incorrectly filled.";
      }
    },
    clearFetchRentalItems: (state: IRentalItemsState) => {
      state.regRentalItemErrs = new RegRentalItemErrs();
      state.fetchRentalItemsErr = null;
    },

    clearFetchRentalItemErrs: (state: IRentalItemsState) => {
      state.regRentalItemErrs = new RegRentalItemErrs();
      state.fetchRentalItemsErr = null;
    },

    fetchRentalItemsStart: (state: IRentalItemsState) => {
      state.fetchingRentalItems = true;
    },
    fetchRentalItemsPass: (
      state: IRentalItemsState,
      action: PayloadAction<IRentalItem[]>,
    ) => {
      state.fetchingRentalItems = false;

      state.rentalItems = action.payload;
    },
    fetchRentalItemsFail: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.fetchingRentalItems = false;
      state.fetchRentalItemsErr = action.payload;
    },
    setMainRentalItem: (
      state: IRentalItemsState,
      action: PayloadAction<IRentalItem | null>,
    ) => {
      state.mainRentalItem = action.payload;
    },
    clearUpdateRentalItemFieldErr: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.updateRentalItemErrs[action.payload as keyof RegRentalItemErrs] =
        null;
    },
    clearUpdateRentalItemErrs: (state: IRentalItemsState) => {
      state.updateRentalItemErrs = new RegRentalItemErrs();
    },
    tryResetUpdateRentalItemFormErr: (state: IRentalItemsState) => {
      let shouldReset = true;

      for (let key in state.updateRentalItemErrs) {
        if (key === "form") {
          continue;
        }

        if (state.updateRentalItemErrs[key as keyof RegRentalItemErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.updateRentalItemErrs.form = null;
      }
    },
    updateRentalItemStart: (state: IRentalItemsState) => {
      state.updatingRentalItem = true;
    },
    updateRentalItemPass: (
      state: IRentalItemsState,
      action: PayloadAction<IRentalItem>,
    ) => {
      state.updatingRentalItem = false;
      // state.mainCustomer = action.payload;
    },
    updateRentalItemFail: (
      state: IRentalItemsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.updatingRentalItem = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updateRentalItemErrs) {
          if (err.field === key) {
            state.updateRentalItemErrs[key as keyof RegRentalItemErrs] =
              err.msg;
            break;
          }
        }
      }

      if (!state.updateRentalItemErrs.form) {
        state.updateRentalItemErrs.form = "Form incorrectly filled.";
      }
    },
    clearToggleLikeErr: (state: IRentalItemsState) => {
      state.toggleLikeErr = null;
    },
    toggleLikeStart: (state: IRentalItemsState) => {
      state.toggleLikeErr = null;
      state.togglingLike = true;
    },
    toggleLikePass: (
      state: IRentalItemsState,
      action: PayloadAction<IRentalItem[]>,
    ) => {
      state.favouriteRentalItems = action.payload;
      state.togglingLike = false;
    },
    toggleLikeFail: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.toggleLikeErr = action.payload;
      state.togglingLike = false;
    },
    setFavouriteRentalItems: (
      state: IRentalItemsState,
      action: PayloadAction<IRentalItem[]>,
    ) => {
      state.favouriteRentalItems = action.payload;
    },
    clearExportRentalItemsFieldErr: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.exportRentalItemsErrs[
        action.payload as keyof ExportRentalItemsErrs
      ] = null;
    },
    clearExportRentalItemsErrs: (state: IRentalItemsState) => {
      state.exportRentalItemsErrs = new ExportRentalItemsErrs();
    },
    tryResetExportRentalItemsFormErr: (state: IRentalItemsState) => {
      let shouldReset = true;

      for (let key in state.exportRentalItemsErrs) {
        if (key === "form") {
          continue;
        }

        if (state.exportRentalItemsErrs[key as keyof ExportRentalItemsErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.exportRentalItemsErrs.form = null;
      }
    },
    exportRentalItemsStart: (state: IRentalItemsState) => {
      state.exportingRentalItems = true;
      state.exportedRentalItems = false;
    },
    exportRentalItemsPass: (state: IRentalItemsState) => {
      state.exportingRentalItems = false;
      state.exportedRentalItems = true;
    },
    exportRentalItemsFail: (
      state: IRentalItemsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.exportingRentalItems = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.exportRentalItemsErrs) {
          if (err.field === key) {
            state.exportRentalItemsErrs[key as keyof ExportRentalItemsErrs] =
              err.msg;
            break;
          }
        }
      }

      if (!state.exportRentalItemsErrs.form) {
        state.exportRentalItemsErrs.form = "Form incorrectly filled.";
      }
    },
    clearImportRentalItemsFieldErr: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.importRentalItemsErrs = [];
    },
    clearImportRentalItemsErrByIndex: (
      state: IRentalItemsState,
      action: PayloadAction<number>,
    ) => {
      const index = action.payload;

      let newErrs = [];

      if (index < state.importRentalItemsErrs.length) {
        for (let i = 0; i < index; i++) {
          newErrs[i] = state.importRentalItemsErrs[i];
        }

        for (let i = index; i < state.importRentalItemsErrs.length - 1; i++) {
          newErrs[i] = state.importRentalItemsErrs[i + 1];
        }

        state.importRentalItemsErrs = newErrs;
      }
    },
    clearResetTriedImportingRentalItems: (state: IRentalItemsState) => {
      state.triedImportingRentalItems = false;
    },
    clearImportRentalItemsErrs: (state: IRentalItemsState) => {
      state.importRentalItemsErrs = [];
    },
    tryResetImportRentalItemsFormErr: (state: IRentalItemsState) => {},
    importRentalItemsStart: (state: IRentalItemsState) => {
      state.importRentalItemsErrs = [];

      state.importingRentalItems = true;
      state.triedImportingRentalItems = false;
      state.importedRentalItems = false;
    },
    importRentalItemsDone: (state: IRentalItemsState) => {
      state.importingRentalItems = false;
      state.triedImportingRentalItems = true;
    },
    importRentalItemsPass: (state: IRentalItemsState) => {
      state.importingRentalItems = false;
      state.triedImportingRentalItems = true;
      state.importedRentalItems = true;
    },
    addErr2ImportRentalItemsErrs: (
      state: IRentalItemsState,
      action: PayloadAction<RegRentalItemErrs>,
    ) => {
      let errs = state.importRentalItemsErrs;

      errs = [...errs, action.payload];

      state.importRentalItemsErrs = errs;
    },
    importRentalItemsFail: (state: IRentalItemsState) => {
      state.importingRentalItems = false;
    },
    clearDeletRentalItemErr: (state: IRentalItemsState) => {
      state.deleteRentalItemErr = null;
    },
    resetDeletedRentalItem: (state: IRentalItemsState) => {
      state.deletedRentalItem = false;
    },
    deleteRentalItemStart: (state: IRentalItemsState) => {
      state.deleteRentalItemErr = null;
      state.deletedRentalItem = false;
      state.deletingRentalItem = true;
    },
    deleteRentalItemPass: (state: IRentalItemsState) => {
      state.deletedRentalItem = true;
      state.deletingRentalItem = false;
    },
    deleteRentalItemFail: (
      state: IRentalItemsState,
      action: PayloadAction<string>,
    ) => {
      state.deleteRentalItemErr = action.payload;
      state.deletingRentalItem = false;
    },
  },
});

export const {
  clearRegRentalItemFieldErr,
  clearRegRentalItemErrs,
  tryResetRegRentalItemFormErr,
  regRentalItemStart,
  regRentalItemPass,
  regRentalItemFail,
  clearFetchRentalItems,
  fetchRentalItemsStart,
  fetchRentalItemsPass,
  fetchRentalItemsFail,
  setMainRentalItem,
  clearFetchRentalItemErrs,
  clearUpdateRentalItemFieldErr,
  clearUpdateRentalItemErrs,
  tryResetUpdateRentalItemFormErr,
  updateRentalItemStart,
  updateRentalItemPass,
  updateRentalItemFail,
  setFavouriteRentalItems,
  clearToggleLikeErr,
  toggleLikeStart,
  toggleLikePass,
  toggleLikeFail,
  clearImportRentalItemsErrByIndex,
  clearExportRentalItemsErrs,
  clearExportRentalItemsFieldErr,
  tryResetExportRentalItemsFormErr,
  exportRentalItemsStart,
  exportRentalItemsPass,
  exportRentalItemsFail,
  clearResetTriedImportingRentalItems,
  clearImportRentalItemsErrs,
  clearImportRentalItemsFieldErr,
  tryResetImportRentalItemsFormErr,
  importRentalItemsStart,
  importRentalItemsDone,
  importRentalItemsPass,
  addErr2ImportRentalItemsErrs,
  importRentalItemsFail,
  clearDeletRentalItemErr,
  resetDeletedRentalItem,
  deleteRentalItemStart,
  deleteRentalItemPass,
  deleteRentalItemFail,
} = rentalItemsSlice.actions;

interface ITryRegRentalItemArg {
  data: FormData;
  authToken: string;
  setRegistered: (truth: boolean) => void;
  setServiceResourceId?: (_id: string | null) => void;
}

export const tryRegRentalItem = createAsyncThunk(
  "rentalItems/tryRegRentalItem",
  (arg: ITryRegRentalItemArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-item`;

    const { data, authToken, setRegistered, setServiceResourceId } = arg;

    dispatch(regRentalItemStart());

    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);

        if (res?.data?.serviceResourceId && setServiceResourceId) {
          setServiceResourceId(res?.data?.serviceResourceId);
        }

        dispatch(regRentalItemPass());
        setRegistered(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(regRentalItemFail(fieldErrors));
      });
  },
);

interface ITryFetchRentalItemArg {
  authToken?: string;
  type?: string;
}

export const tryFetchRentalItems = createAsyncThunk(
  "rentalItems/tryFetchRentalItems",
  (arg: ITryFetchRentalItemArg, { dispatch }) => {
    const { authToken, type } = arg;

    const endpoint = `${
      process.env.REACT_APP_SERVER_ENDPOINT
    }/api/rental-items${type ? `?type=${type}` : ""}`;

    dispatch(fetchRentalItemsStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      })
      .then((res) => {
        dispatch(fetchRentalItemsPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired" && authToken) {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.err) {
          dispatch(fetchRentalItemsFail(data.err));
        } else {
          dispatch(fetchRentalItemsFail("Check your network connection."));
        }
      });
  },
);

interface ITryUpdateRentalItemArg {
  rentalItemId: string;
  data: FormData;
  authToken: string;
  setUpdated: (truth: boolean) => void;
}

export const tryUpdateRentalItem = createAsyncThunk(
  "rentalItems/tryUpdateRentalItem",
  (arg: ITryUpdateRentalItemArg, { dispatch }) => {
    const { rentalItemId, data, authToken, setUpdated } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-items/${rentalItemId}`;

    dispatch(updateRentalItemStart());

    axios
      .put(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateRentalItemPass(res.data));
        setUpdated(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(updateRentalItemFail(fieldErrors));
      });
  },
);

interface ITryToggleRentalItemLikeArg {
  authToken: string;
  rentalItemId: string;
}

export const tryToggleRentalLike = createAsyncThunk(
  "rentalItems/tryToggleLike",
  (arg: ITryToggleRentalItemLikeArg, { dispatch }) => {
    const { authToken, rentalItemId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-items/${rentalItemId}/toggle-like`;

    dispatch(toggleLikeStart());
    axios
      .post(
        endpoint,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        },
      )
      .then((res) => {
        dispatch(toggleLikePass(res.data?.favouriteRentalItems ?? []));
      })
      .catch((err) => {
        const data = err?.response?.data;
        let error = "";

        if (data?.errMsg) {
          error = data.errMsg;
        } else if (data?.errors) {
          error = getFieldErrors(data.errors)[0].msg;
        } else {
          error = "Check your network connection.";
        }

        dispatch(toggleLikeFail(error));
      });
  },
);

interface ITryExportRentalItemsArg {
  format: string;
  authToken: string;
}

export const tryExportRentalItems = createAsyncThunk(
  "servicesPlans/tryExportRentalItems",
  (arg: ITryExportRentalItemsArg, { dispatch }) => {
    const { authToken, format } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-items-export`;

    dispatch(exportRentalItemsStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        const data = res.data;

        if (data.length === 0) {
          const fieldErrors = [{ field: "form", msg: "Export Empty." }];

          dispatch(exportRentalItemsFail(fieldErrors));
        } else {
          switch (format) {
            case XLSX:
            default:
              const fileSettings = {
                fileName: "RentalItems", //TODO: dynamically generate the name, based on the parameters used to fetch the data
              };

              try {
                xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
              } catch (err) {
                console.log(err);
              }
              break;
          }

          dispatch(exportRentalItemsPass());
        }
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          console.log(data);
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(exportRentalItemsFail(fieldErrors));
      });
  },
);

interface ITryImportRentalItemsArg {
  format: string;
  authToken: string;
  data: any[];
}

export const tryImportRentalItems = createAsyncThunk(
  "rentalItems/tryImportRentalItems",
  (arg: ITryImportRentalItemsArg, { dispatch }) => {
    const { authToken, format, data } = arg;

    let errors: RegRentalItemErrs[] = [];

    let itr = 0;
    let successCount = 0;

    for (const rentalItemData of data) {
      dispatch(importRentalItemsStart());

      const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-item`;

      let data4From: any = null;

      try {
        switch (format) {
          case XLSX:
          default:
            data4From = rentalItemExcelData2DBData(rentalItemData);
            break;
        }
      } catch (err) {
        dispatch(importRentalItemsFail());
        alert(`Error : ${err}`);
        return;
      }

      let newData = new FormData();
      let registered = false;

      for (const key in data4From) {
        newData.append(key, data4From[key as keyof typeof data4From]);
      }

      axios
        .post(endpoint, newData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((res) => {
          ++successCount;
          alert(`Successfully imported Rental Item in row ${itr + 1}`);

          dispatch(importRentalItemsDone());
        })
        .catch((err) => {
          const errData = err?.response?.data;

          let fieldErrors: IFieldError[] = [];

          if (errData?.errors) {
            fieldErrors = getFieldErrors(errData.errors);
          } else if (errData?.err) {
            fieldErrors = [{ field: "form", msg: errData.err }];
          } else {
            fieldErrors = [
              { field: "form", msg: "Check your network connection." },
            ];
          }

          let regErrs = new RegRentalItemErrs();

          for (const error of fieldErrors) {
            for (const key in regErrs) {
              if (error.field === key) {
                regErrs[key as keyof RegRentalItemErrs] = error.msg;
                break;
              }
            }
          }

          regErrs.index = itr;

          dispatch(addErr2ImportRentalItemsErrs(regErrs));

          if (itr++ === data.length - 1) {
            itr = 0;
            dispatch(importRentalItemsDone());
          }
        });
    }
  },
);

interface IDeleteRentalItemArg {
  authToken: string;
  rentalItemId: string;
}

export const tryDeleteRentalItem = createAsyncThunk(
  "rentalItems/deleteRentalItem",
  (arg: IDeleteRentalItemArg, { dispatch }) => {
    const { authToken, rentalItemId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-items/${rentalItemId}`;

    dispatch(deleteRentalItemStart());
    axios
      .delete(endpoint, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        dispatch(deleteRentalItemPass());
        socket.emit(APP_UPDATE_AVAILABLE, USER);
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }
        let error = "";

        if (data?.err) {
          error = data.err;
        } else if (data?.errors) {
          error = getFieldErrors(data.errors)[0].msg;
        } else {
          error = "Check your network connection.";
        }

        dispatch(deleteRentalItemFail(error));
      });
  },
);

export default rentalItemsSlice.reducer;
